import { getCatComponents as scanCatComponents, getCatRoomComponents as scanCatRoomComponents } from "./components";

export {
    applyImportedRoomMigrationIfNeeded,
    applyBasementRoomMigrationIfNeeded,
    normalizeImportedRoomAssignments,
    normalizeBasementRoomAssignments,
    ROOM_ASSIGNMENT_MIGRATION_VERSION,
} from "./normalizeImportedRoomAssignments";

export {
    calcDefaultRoomArea,
    calcDefaultRoomHeight,
    calcPendingSplitRoomMeasurements,
    calcSplitDrawerDefaultRoomArea,
    isRoomMeasurementUnset,
} from "./roomDefaultMeasurements";

export {
    getCatRoomSubComponents,
    getNextComponentDisplayOrder,
    getComponentsSortedByRoom,
} from "./components";

export { 
    getDupeComponentData,
    getDupeSubComponentsData,
    getStoreyOptions,
} from "./duplicateRoom";

// export { applySplitDrawerRoomMeasurements } from "./splitComponentFunctions";

const ROOM_ASSIGNABLE_COMPONENT_TYPES = ["basement", "crawlspace", "slab", "wall", "ceiling", "expFloor"];

export const isDefaultRoomId = (roomId = "", catId = "") => roomId === `room-default-${catId}`;

export const isUserDefinedRoomId = (roomId = "") => !!roomId && !roomId.includes("room-default");

export const hasUserDefinedRooms = (roomSummary) =>
    Object.values(roomSummary || {}).some((catRooms) => Object.keys(catRooms || {}).some(isUserDefinedRoomId));

// Room definitions on their own don't mean the model is being modelled room by room: h2k imports can
// arrive with rooms that no component has been sorted into.
export const hasComponentsAssignedToRooms = (components, roomSummary) => {
    const userDefinedRoomIds = new Set(
        Object.values(roomSummary || {}).flatMap((catRooms) => Object.keys(catRooms || {}).filter(isUserDefinedRoomId))
    );

    if (userDefinedRoomIds.size === 0) {
        return false;
    }

    return ROOM_ASSIGNABLE_COMPONENT_TYPES.some((componentType) =>
        Object.values(components?.[componentType] || {}).some((comp) => userDefinedRoomIds.has(comp?.roomId))
    );
};

export const sortRoomsByName = (([keyA, roomDataA], [keyB, roomDataB]) => {
    const nameA = roomDataA?.name?.trim() || "";
    const nameB = roomDataB?.name?.trim() || "";

    return nameA.localeCompare(nameB, undefined, { numeric: true });
});

// Return a sorted list of rooms within a particular storey
export const getCatRooms = (roomSummary = {}, catId = "", availableCategories = []) => {
    const currentCatRooms = Object.entries(roomSummary[catId] || {});

    // Put rooms with unavailable categories into "other"
    var hiddenRooms =
        catId === "other"
            ? Object.entries(roomSummary)
                .filter(([key, value]) => !availableCategories.includes(key))
                .reduce((acc, [curKey, curValue]) => {
                    // Exclude default "unassigned" rooms and save room's original category
                    const userDefRooms = Object.entries(curValue)
                        .filter(([roomKey, roomValue]) => isUserDefinedRoomId(roomKey))
                        .reduce((acc, [curRoomKey, curRoomValue]) => {
                            return [...acc, [curRoomKey, { ...curRoomValue, catId: curKey }]];
                        }, []);
                    return [...acc, ...userDefRooms];
                }, [])
            : [];

    return [...currentCatRooms, ...hiddenRooms]
        .sort(([keyA, roomDataA], [keyB, roomDataB]) => {
            // Keep default room to bottom
            if (keyA === `room-default-${catId}`) return 1;
            if (keyB === `room-default-${catId}`) return -1;

            // Place room with higher order number lower
            if (roomDataA?.displayOrder !== roomDataB?.displayOrder) {
                return roomDataA.displayOrder - roomDataB.displayOrder;
            }

            // Sort alphabetically by name for rooms with the same display order
            // i.e. rooms created in older models
            return sortRoomsByName([keyA, roomDataA], [keyB, roomDataB]);
        })
        .map(([key, val]) => {
            return {
                id: key,
                roomId: key,
                catId: val.catId || catId,
                roomName: val.name,
                displayOrder: val.displayOrder,
                area: val.area,
                height: val.height,
                intGainsFraction: val.internalGainsFraction,
            };
        });
};

// Get all user defined rooms in a particular storey
export const getUserDefinedRooms = (roomSummary, catId, availableCategories) => {
    return getCatRooms(roomSummary, catId, availableCategories)
        .filter(({ roomId }) => isUserDefinedRoomId(roomId));
}

// Find the next available display order for a new room
export const getNextRoomDisplayOrder = (roomSummary, catId, availableCategories) => {
    const userDefinedRooms = getUserDefinedRooms(roomSummary, catId, availableCategories);
    return userDefinedRooms.length;
}

export const getRoomName = (roomSummary, catId, roomId) => {
    return roomSummary[catId]?.[roomId]?.name || "";
}

// Caches storey/room component scans per `componentsObject`. Immer gives us a fresh
// components object whenever any component changes, so a WeakMap keyed on it both
// invalidates itself and keeps the returned arrays referentially stable in between,
// which lets React.memo boundaries downstream actually hold.
// Callers must treat the returned array (and its items) as read-only.

const componentScanCache = new WeakMap();

const cacheByComponentsObject =
    (name, scan) =>
    (componentsObject = {}, ...args) => {
        let scansForObject = componentScanCache.get(componentsObject);

        if (!scansForObject) {
            scansForObject = new Map();
            componentScanCache.set(componentsObject, scansForObject);
        }

        const cacheKey = [name, ...args.map((arg) => (Array.isArray(arg) ? arg.join(",") : String(arg)))].join("|");

        if (!scansForObject.has(cacheKey)) {
            scansForObject.set(cacheKey, scan(componentsObject, ...args));
        }

        return scansForObject.get(cacheKey);
    };

// Return a sorted list of components assigned to a particular room
export const getCatRoomComponents = cacheByComponentsObject("catRoomComponents", scanCatRoomComponents);

// Return a sorted list of components within a particular storey
export const getCatComponents = cacheByComponentsObject("catComponents", scanCatComponents);
